import {
  ENDPOINTS,
  ERROR_STATUS,
  MAX_ENVELOPE_BYTES,
  tooLargeMessage,
  PROTOCOL_PREFIX,
  checkProtocolVersion,
  type Endpoint,
} from "@byollm/protocol";
import { ByollmHandlers, type HandlerConfig } from "./handlers.js";

/**
 * Largest protocol request body accepted, before schema validation.
 *
 * Derived, not chosen. This was `8 * 1024 * 1024` beside a comment saying the
 * protocol caps a payload at 4 MB — true when it was written, and the cap has
 * since moved to {@link MAX_ENVELOPE_BYTES}, which is 6 MiB. So the direct
 * lane refused envelopes the protocol permits, and the hub — which derives
 * its own limit the same way this now does — accepted them.
 *
 * That is the failure this codebase keeps finding in other clothes: one rule
 * with two implementations, and only one of them moved. A site self-hosting
 * the SDK and a site on the hub must not disagree about whether a job is too
 * big, so neither of them gets to hold the number.
 *
 * The 512 KiB of headroom is for JSON overhead and a batch of results around
 * the envelope, matching the hub's `MAX_ENVELOPE_BYTES + 512 * 1024`.
 */
const MAX_BODY_BYTES = MAX_ENVELOPE_BYTES + 512 * 1024;

/**
 * What a message that is too big is told — B061, Kevin's bisection.
 *
 * This said "request body too large" and nothing else, at both call sites,
 * with both numbers already in scope. Somebody who hits it learns that
 * something was too big and not what, not by how much, not whether the limit
 * is per-message or per-account, and not what to do — so the only way
 * forward is to bisect, which is exactly what Kevin did.
 *
 * The sentence and its rounding come from the protocol now — B072. The first
 * version of this copied the relay's WORDS and rediscovered the relay's bug
 * with them: `toFixed` rounds to nearest, so one byte over printed "this
 * message is 10.5 MB and the limit is 10.5 MB". The relay had already found
 * that, fixed it, and written the reasoning beside the fix. Copying a
 * sentence copies everything about it except the part that was learned.
 */
const tooLarge = (bytes: number): string =>
  tooLargeMessage({ bytes, limit: MAX_BODY_BYTES });

/**
 * Where the protocol endpoints are mounted.
 *
 * Defaults to {@link PROTOCOL_PREFIX}. Pass the real mount point when it is
 * anything else — a Next.js route at `app/api/byollm/[...route]/route.ts`
 * serves `/api/byollm/...`, so it needs `basePath: "/api/byollm"`.
 *
 * @throws if the path is not an absolute, single-segment-per-slash path. A
 * mount point is configuration, and a malformed one should fail at startup
 * rather than silently match nothing.
 */
function normalizeBasePath(basePath: string): string {
  const trimmed = basePath.endsWith("/") ? basePath.slice(0, -1) : basePath;
  if (!trimmed.startsWith("/")) {
    throw new Error(`basePath must start with "/": got ${basePath}`);
  }
  if (trimmed.includes("//") || /[?#*]/.test(trimmed)) {
    throw new Error(`basePath must be a plain path: got ${basePath}`);
  }
  return trimmed;
}

/**
 * Pull the endpoint name out of a URL path, or null if it isn't ours.
 *
 * The full path must match `<basePath>/<endpoint>` exactly. This used to
 * compare only the *last* segment, which meant `/anything/at/all/claim`
 * dispatched to `claim` and {@link PROTOCOL_PREFIX} was decorative — it
 * appeared in a 404 message and was never matched against. For the handler
 * that serves claim, result and heartbeat, dispatching on a suffix is a
 * looser rule than anyone reading the constant would assume, and loose
 * matching in a security surface should at least be a decision.
 *
 * The cost is that the mount point is now something a deployment has to state
 * rather than something that works by accident. That is the intended trade:
 * a 404 at startup naming the mount point beats a handler answering on paths
 * nobody meant to expose.
 */
export function routeEndpoint(
  pathname: string,
  basePath: string = PROTOCOL_PREFIX,
): Endpoint | null {
  const base = normalizeBasePath(basePath);
  const path = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (!path.startsWith(`${base}/`)) return null;
  const rest = path.slice(base.length + 1);
  return (ENDPOINTS as readonly string[]).includes(rest)
    ? (rest as Endpoint)
    : null;
}

/**
 * Read the request signature from headers (byollm_009 §4.2).
 *
 * In headers rather than the body so the signature covers the body whole,
 * with no field to exclude from its own hash — a scheme that signs a body
 * minus one field has to agree, byte for byte, on how that field is removed.
 */
export function signatureFrom(headers: Headers): unknown {
  const runnerId = headers.get("x-byollm-runner");
  const rawIssuedAt = headers.get("x-byollm-issued-at");
  const signature = headers.get("x-byollm-signature");
  if (runnerId === null || signature === null || rawIssuedAt === null) {
    return undefined;
  }
  // Checked against null *before* Number(), because `Number(null)` is 0 —
  // finite, plausible-looking, and wrong. A missing timestamp would have
  // become a timestamp of the epoch, which the freshness check would then
  // reject for the wrong reason.
  const issuedAt = Number(rawIssuedAt);
  if (!Number.isFinite(issuedAt)) return undefined;
  return { runnerId, issuedAt, signature };
}

/**
 * A `Request` → `Response` handler for the whole protocol.
 *
 * Web-standard types, so this works unchanged in Next.js route handlers, Hono,
 * Bun, Deno, Cloudflare Workers, and anything else that speaks fetch.
 */
export function createFetchHandler(
  config: HandlerConfig & {
    /**
     * Where these endpoints are mounted. Defaults to
     * {@link PROTOCOL_PREFIX}; set it when the app serves them elsewhere.
     */
    readonly basePath?: string;
  },
): (request: Request) => Promise<Response> {
  const handlers = new ByollmHandlers(config);
  // Validate once, at construction: a bad mount point is a deployment bug and
  // should surface when the server starts, not as a silent 404 per request.
  const basePath = normalizeBasePath(config.basePath ?? PROTOCOL_PREFIX);

  return async function handle(request: Request): Promise<Response> {
    if (request.method !== "POST") {
      return json(405, {
        error: "bad-request",
        message: "protocol endpoints accept POST only",
      });
    }

    const endpoint = routeEndpoint(new URL(request.url).pathname, basePath);
    if (endpoint === null) {
      return json(404, {
        error: "not-found",
        message: `not a ${basePath} endpoint`,
      });
    }

    const declared = request.headers.get("content-length");
    if (declared !== null && Number(declared) > MAX_BODY_BYTES) {
      return json(400, {
        error: "bad-request",
        message: tooLarge(Number(declared)),
      });
    }

    let body: unknown;
    let rawBody: string;
    try {
      rawBody = await request.text();
      const text = rawBody;
      if (text.length > MAX_BODY_BYTES) {
        return json(400, {
          error: "bad-request",
          message: tooLarge(text.length),
        });
      }
      body = JSON.parse(text);
    } catch {
      // Deliberately not echoing the parse error: it would quote attacker
      // input back into a response an operator later reads in a terminal.
      return json(400, {
        error: "bad-request",
        message: "request body is not valid JSON",
      });
    }

    // byollm_009 §4: version before anything else. A mismatch must name the
    // disagreement and the fix, not surface as a generic bad-request from a
    // schema literal buried in an endpoint — which is what happened before,
    // and is why "the connection is versionless" was listed as a defect.
    const refusal = checkProtocolVersion(body);
    if (refusal) {
      return json(ERROR_STATUS[refusal.error], refusal);
    }

    const result = await handlers.handle(endpoint, body, {
      endpoint,
      // The bytes as received. Re-serialising the parsed object would verify
      // a signature over something the sender never sent.
      rawBody,
      signature: signatureFrom(request.headers),
    });

    const headers: Record<string, string> = {
      "content-type": "application/json",
      "cache-control": "no-store",
    };
    if (result.retryAfterSeconds !== undefined) {
      headers["retry-after"] = String(result.retryAfterSeconds);
    }
    return new Response(JSON.stringify(result.body), {
      status: result.status,
      headers,
    });
  };
}

function json(status: number, body: unknown): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: {
      "content-type": "application/json",
      "cache-control": "no-store",
    },
  });
}
